import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const priorityColors = {
  low: 'default',
  normal: 'primary',
  high: 'warning',
  urgent: 'error',
};

const typeColors = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
  general: 'default',
};

const channelColors = {
  'in-app': 'primary',
  email: 'secondary',
  sms: 'info',
  push: 'warning',
};

function NotificationList({
  notifications = [],
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onView,
  onImport,
  selectedNotification,
  onSelectionChange,
  onMarkRead,
  onMarkUnread,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch = notification.message?.toLowerCase().includes(searchTerm.toLowerCase())
      || notification.related?.toLowerCase().includes(searchTerm.toLowerCase())
      || notification.type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    const matchesPriority = priorityFilter === 'all' || notification.priority === priorityFilter;
    const matchesChannel = channelFilter === 'all' || notification.channel === channelFilter;
    const matchesRead = readFilter === 'all'
      || (readFilter === 'read' && notification.read)
      || (readFilter === 'unread' && !notification.read);

    return matchesSearch && matchesType && matchesPriority && matchesChannel && matchesRead;
  });

  const handleExport = () => {
    const csvContent = [
      ['Message', 'Type', 'Priority', 'Channel', 'Related', 'Read', 'Time', 'Created At'],
      ...filteredNotifications.map((notification) => [
        notification.message || '',
        notification.type || '',
        notification.priority || '',
        notification.channel || '',
        notification.related || '',
        notification.read ? 'Yes' : 'No',
        notification.time || '',
        notification.createdAt ? formatDate(notification.createdAt) : '',
      ]),
    ].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'notifications.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
            {params.value?.substring(0, 100)}
            {params.value?.length > 100 && '...'}
          </Typography>
          {params.row.related && (
            <Typography variant="caption" color="text.secondary">
              Related:
              {' '}
              {params.row.related}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'General'}
          size="small"
          color={typeColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Normal'}
          size="small"
          color={priorityColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'channel',
      headerName: 'Channel',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'In-App'}
          size="small"
          color={channelColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'read',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Read' : 'Unread'}
          size="small"
          color={params.value ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'time',
      headerName: 'Scheduled',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => onView(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={params.row.read ? <UnreadIcon /> : <ReadIcon />}
          label={params.row.read ? 'Mark Unread' : 'Mark Read'}
          onClick={() => (params.row.read ? onMarkUnread(params.row) : onMarkRead(params.row))}
          color="info"
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEdit(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => onDelete(params.row)}
          color="error"
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      {/* Header with search and filters */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h2">
            Notifications (
            {filteredNotifications.length}
            )
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={filteredNotifications.length === 0}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Add Notification
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="all">All Priorities</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={channelFilter}
              label="Channel"
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              <MenuItem value="all">All Channels</MenuItem>
              <MenuItem value="in-app">In-App</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="push">Push</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={readFilter}
              label="Status"
              onChange={(e) => setReadFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="read">Read</MenuItem>
              <MenuItem value="unread">Unread</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Import Notifications">
            <IconButton onClick={onImport} color="primary">
              <MoreIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={filteredNotifications}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
}

export default NotificationList;
